import {HttpClient} from '@angular/common/http';
import {Injectable, OnDestroy, signal} from '@angular/core';
import {Observable, of, Subscription} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {BookTimeNowTimeSlot} from '../types/book-time-now-slot';
import {RouterLinkAndParams} from '../types/router-link-and-params';
import {DefaultValuesService} from '../../../services/default-values.service';
import {RoutesEnums} from '../../../enums/RoutesEnums';
import {UrlBuilderService} from '../../../services/url-builder.service';
import {ToolsService} from '../../../services/tools.service';
import {AppConfigService} from '../../../services/app-config.service';

@Injectable()
export class BookTimeNowService implements OnDestroy {
  public careproviderTimeslots = signal<BookTimeNowTimeSlot[]>([]);
  public careservicetypeTimeslots = signal<BookTimeNowTimeSlot[]>([]);
  public symptomTimeslots = signal<BookTimeNowTimeSlot[]>([]);

  private loadedCareProviderUnitId: number;
  private loadedCareServiceId: number;
  private loadedSymptomId: number;
  private loadedLat: number;
  private loadedLon: number;
  private subscriptions: Subscription[] = [];

  constructor(
    private http: HttpClient,
    private defaults: DefaultValuesService,
    private urlBuilder: UrlBuilderService,
    private tools: ToolsService,
    private appConfigService: AppConfigService,
  ) {}

  public ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public reloadCareProviderTimeslots(careProviderUnitId: number): void {
    if (this.loadedCareProviderUnitId !== careProviderUnitId) {
      this.loadedCareProviderUnitId = careProviderUnitId;
      this.subscriptions.push(
        this.getCareProviderTimeslots(careProviderUnitId).subscribe((timeslots) => {
          this.careproviderTimeslots.set(timeslots);
        }),
      );
    }
  }

  public reloadCareServiceTimeslots(careServiceId: number, lat: number, lon: number): void {
    if (this.loadedCareServiceId !== careServiceId || this.loadedLat !== lat || this.loadedLon !== lon) {
      this.loadedCareServiceId = careServiceId;
      this.loadedLat = lat;
      this.loadedLon = lon;
      this.subscriptions.push(
        this.getCareServiceTimeslots(careServiceId, lat, lon).subscribe((timeslots) => {
          this.careservicetypeTimeslots.set(timeslots);
        }),
      );
    }
  }

  public reloadSymptomsTimeslots(symptomId: number, lat: number, lon: number): void {
    if (this.loadedSymptomId !== symptomId || this.loadedLat !== lat || this.loadedLon !== lon) {
      this.loadedSymptomId = symptomId;
      this.loadedLat = lat;
      this.loadedLon = lon;
      this.subscriptions.push(
        this.getSymptomTimeslots(symptomId, lat, lon).subscribe((timeslots) => {
          this.symptomTimeslots.set(timeslots);
        }),
      );
    }
  }

  public getSeeMoreUrlParams(
    caregiverUnitTypeUrlPath: string,
    careServiceTypeUrlPath: string,
    symptomUrlPath: string,
    lat: number,
    lon: number,
  ): RouterLinkAndParams {
    const state = this.defaults.getDefaultSearchState(caregiverUnitTypeUrlPath, careServiceTypeUrlPath);
    state.lat = lat;
    state.lon = lon;
    state.locationSearch = true;
    if (!this.tools.isNil(caregiverUnitTypeUrlPath) && caregiverUnitTypeUrlPath !== '') {
      state.caregiverUnitTypeUrlPath = caregiverUnitTypeUrlPath;
    }
    if (!this.tools.isNil(careServiceTypeUrlPath) && careServiceTypeUrlPath !== '') {
      state.careServiceTypeUrlPath = careServiceTypeUrlPath;
    }
    if (!this.tools.isNil(symptomUrlPath) && symptomUrlPath !== '') {
      state.symptomUrlPath = symptomUrlPath;
    }
    return {
      routerLink: this.urlBuilder.CreateSearchResultsRouterLink(state, ['/', RoutesEnums.SEARCH], false),
      queryParams: this.urlBuilder.CreateSearchResultsQueryParams(state),
    };
  }

  private getCareProviderTimeslots(careProviderUnitId: number): Observable<BookTimeNowTimeSlot[]> {
    const endpoint = `${this.appConfigService.appConfig.apiUrl}search/careprovider/${careProviderUnitId}/booktimenow/timeslots`;

    return this.http.get<BookTimeNowTimeSlot[]>(endpoint).pipe(catchError((error) => of(null)));
  }

  private getCareServiceTimeslots(careServiceId: number, lat: number, lon: number): Observable<BookTimeNowTimeSlot[]> {
    let endpoint = `search/careservicetype/${careServiceId}/booktimenow/timeslots`;
    if (!this.tools.isNil(lat) && !this.tools.isNil(lon)) {
      endpoint = `${this.appConfigService.appConfig.apiUrl}${endpoint}?lat=${lat}&lon=${lon}`;
    } else {
      endpoint = `${this.appConfigService.appConfig.apiUrl}${endpoint}`;
    }

    return this.http.get<BookTimeNowTimeSlot[]>(endpoint).pipe(catchError((error) => of(null)));
  }

  private getSymptomTimeslots(symptomId: number, lat: number, lon: number): Observable<BookTimeNowTimeSlot[]> {
    let endpoint = `search/symptom/${symptomId}/booktimenow/timeslots`;
    if (!this.tools.isNil(lat) && !this.tools.isNil(lon)) {
      endpoint = `${this.appConfigService.appConfig.apiUrl}${endpoint}?lat=${lat}&lon=${lon}`;
    } else {
      endpoint = `${this.appConfigService.appConfig.apiUrl}${endpoint}`;
    }

    return this.http.get<BookTimeNowTimeSlot[]>(endpoint).pipe(catchError((error) => of(null)));
  }
}
